<script setup lang='ts'>
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { useFiltersStore } from '@/stores/useFiltersStore';
import { useGlobalConfigurationStore } from '@/stores/useGlobalConfigurationStore';
import { useProductsStore } from '@/stores/useProductsStore';

import TheCard from './TheCard.vue';

const { t } = useI18n();

const globalConfigurationStore = useGlobalConfigurationStore();
const { globalConfiguration } = storeToRefs(globalConfigurationStore);

const productsStore = useProductsStore();
const { products, totalResults } = storeToRefs(productsStore);

const filtersStore = useFiltersStore();
const { filters } = storeToRefs(filtersStore);

const prefixUrl = computed(() => globalConfiguration.value?.prefixUrl ?? null);
const canLoadMore = computed(() => products.value.length < totalResults.value);
const showFavorites = computed(() => globalConfiguration.value?.options?.hasFavorites !== false);

const desktopWidth = ref(window.innerWidth);
const desktopViewport = computed(() => desktopWidth.value >= 768);

const peristedGalleryId = ref<string>('');

function loadMore() {
  productsStore.fetchPaginatedProductsList(filters.value);
}

function handleResize() {
  desktopWidth.value = window.innerWidth;
}

function persistGallery(id: string) {
  peristedGalleryId.value = id;
}

onMounted(() => {
  window.addEventListener('resize', handleResize);
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});
</script>

<template>
  <section v-if="products.length" class="grid_wrapper" :aria-label="t('list.label')">
    <TheCard
      v-for="product in products"
      :key="product.sourceId"
      :product="product"
      :prefix-url="prefixUrl"
      :responsive-row="true"
      :with-slider="true"
      :has-large-viewport="desktopViewport"
      :show-favorite="showFavorites"
      :persisted-product-gallery-id="peristedGalleryId"
      @peristed-gallery="(id: string) => persistGallery(id)"
    />
    <button v-if="canLoadMore" type="button" class="button" :aria-label="t('list.load')" @click="loadMore">
      {{ t('list.load') }}
    </button>
  </section>
  <section v-else class="empty_wrapper">
    <p class="empty_warning">
      {{ t('list.empty') }}
    </p>
    <button type="button" class="button" :aria-label="t('list.reload')" @click="filtersStore.resetFilters">
      {{ t('list.reload') }}
    </button>
  </section>
</template>

<style lang="css" scoped>
.grid_wrapper {
  display: grid;
  width: 100%;
  grid-template-columns: repeat(auto-fill, minmax(var(--yb-card-width), 1fr));
  place-content: space-between;
  gap: 20px;
  background-color: transparent;

  @media screen and (width < 640px) {
    gap: var(--yb-spacing-sm);
  }
}

.empty_wrapper {
  display: grid;
  width: 100%;
  min-height: 30vh;
  place-content: center;
  gap: var(--yb-spacing-base) var(--yb-spacing-sm);
}

.button {
  all: unset;
  grid-column: 1 / -1;
  justify-self: center;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0.8rem var(--yb-spacing-xl);
  background-color: var(--yb-color-primary);
  color: var(--yb-color-white);
  border-radius: var(--yb-radius-full);
  font-family: var(--yb-default-font, sans-serif);
  font-weight: var(--yb-text-semi-bold);
  font-size: var(--yb-text-base);
  cursor: pointer;
  margin-bottom: 50px;
}

.empty_warning {
  color: var(--yb-color-black);
  text-align: center;
}

@media screen and (width < 768px) {
  .grid_wrapper {
    grid-template-columns: 1fr;
  }
}
</style>
